import React from "react";
import { useTranslation } from "react-i18next";
import Button from "@/ui/Button/Button";
import Modal from "@/ui/Modal/Modal";
import { useRaffleDataStore } from "@/apps/raffle/store/raffleDataStore";
import {
  GiftIcon,
  RaffleModalCloseIcon,
} from "@/apps/raffle/pages/RafflePage/icons";
import { formatMoney, RUBLE_SIGN } from "@/apps/raffle/pages/RafflePage/utils";
import { useCurrentRegionName } from "@/apps/goals/hooks/useCurrentRegionName";
import cls from "./styles.module.scss";

type ResultsPopupModalProps = {
  isSubmitting: boolean;
  onDismiss: () => void;
};

export const ResultsPopupModal = ({
  isSubmitting,
  onDismiss,
}: ResultsPopupModalProps) => {
  const { t } = useTranslation();
  const popup = useRaffleDataStore((state) => state.bonusWinPopup);
  const regionName = useCurrentRegionName();

  if (popup?.type !== "results") {
    return null;
  }

  const prizeFund = popup.winners.reduce(
    (total, winner) => total + winner.prize_amount,
    0
  );

  return (
    <Modal
      isOpen
      onClose={onDismiss}
      hideHeader
      wrapperClassName={cls.resultsWrapper}
      wrapperClosingClassName={cls.resultsWrapperClosing}
      contentClassName={cls.resultsContent}
      closeAnimationMs={240}
    >
      <div className={cls.resultsSheet}>
        <button
          type="button"
          className={cls.resultsClose}
          onClick={onDismiss}
          aria-label={t("raffle.bonusPopup.close")}
        >
          <RaffleModalCloseIcon />
        </button>

        <div className={cls.resultsHeader}>
          <div className={cls.regionBlock}>{regionName}</div>

          <h2 className={cls.resultsTitle}>{t("raffle.bonusPopup.title")}</h2>
        </div>

        <div className={cls.summaryBlock}>
          <div className={cls.summaryHeader}>
            <div className={cls.summaryIcon}>
              <GiftIcon />
            </div>

            <div className={cls.summaryMain}>
              <span className={cls.summaryLabel}>
                {t("raffle.bonusPopup.prizeFund")}
              </span>
              <strong className={cls.summaryAmount}>
                {formatMoney(prizeFund)} {RUBLE_SIGN}
              </strong>
            </div>

            <span className={cls.summaryCount}>
              {t("raffle.bonusPopup.prizeCount", {
                count: popup.winners.length,
              })}
            </span>
          </div>
        </div>

        <div className={cls.winnersTable}>
          <div className={cls.winnersList}>
            <div className={cls.winnersHeader}>
              <span>{t("raffle.bonusPopup.winnerColumn")}</span>
              <span>{t("raffle.bonusPopup.prizeColumn")}</span>
            </div>
            {popup.winners.map((winner, index) => (
              <div className={cls.winnerRow} key={`${winner.name}-${index}`}>
                <span className={cls.winnerName}>{winner.name}</span>
                <strong className={cls.winnerPrize}>
                  {formatMoney(winner.prize_amount)} {RUBLE_SIGN}
                </strong>
              </div>
            ))}
          </div>
        </div>

        <Button
          className={cls.resultsButton}
          label={t("raffle.bonusPopup.ok")}
          loading={isSubmitting}
          onClick={onDismiss}
        />
      </div>
    </Modal>
  );
};
